import { memo } from 'react';
import { roundBook, fmtBook } from '../../utils/casinoBook';

const CARD_BASE = 'https://wver.sprintstaticdata.com/v230/static/front/img/cards';

function isSusp(item) {
  return !item || item.gstatus === 'SUSPENDED' || item.gstatus === '0';
}

function suspCls(item) {
  return isSusp(item) ? ' suspended' : '';
}

// 100000 -> 1L, 25000 -> 25K (reference range formatting)
function formatAmount(n) {
  const num = Number(n) || 0;
  if (num >= 100000) return `${num / 100000}L`;
  if (num >= 1000) return `${num / 1000}K`;
  return String(num);
}

function bookOf(exposures, item) {
  const v = exposures?.[`sid:${item?.sid}`] ?? exposures?.[item?.nat];
  return v ? roundBook(v) : 0;
}

function bookCls(v) {
  if (!v) return 'book-black';
  return v < 0 ? 'book-red' : 'book-green';
}

// A locked Low / High or side-bet cell (Even / Odd / Black / Red) with a book
// pins its padlock to the top (style.css `.suspended.lock-top:before`) — user
// 2026-10-15. The card tiles keep the centred padlock.
function lockTopCls(exposures, item) {
  return bookOf(exposures, item) ? ' lock-top' : '';
}

function CasinoBook({ value }) {
  return <div className={`casino-book ${bookCls(value)}`}>{fmtBook(value)}</div>;
}

function MinMax({ item, indent }) {
  return (
    <div className="casino-min-max text-right">
      {`\n${indent}R:`}
      <span>{formatAmount(item?.min)}</span>
      -
      <span>{formatAmount(item?.max)}</span>
    </div>
  );
}

// Display labels for card images; API nats: Card 1 ... Card 10, Card J
const CARDS = [
  ['A', 'Card 1'], ['2', 'Card 2'], ['3', 'Card 3'], ['4', 'Card 4'],
  ['5', 'Card 5'], ['6', 'Card 6'], ['7', 'Card 7'], ['8', 'Card 8'],
  ['9', 'Card 9'], ['10', 'Card 10'], ['J', 'Card J'],
];

// Lucky 6 (lucky5) — reference: casino-table lucky7 layout; low/high + center
// static 6 card, sidebets (Even/Odd/Black/Red suit imgs), 11 card bets.
const BetTableLucky6 = memo(function BetTableLucky6({ gameData, onBet, exposures }) {
  const sub = gameData?.sub || [];
  const handleBet = onBet || (() => {});
  const byNat = (nat) => sub.find((s) => s.nat === nat);

  const lowItem = byNat('Low Card');
  const highItem = byNat('High Card');
  const evenItem = byNat('Even');
  const oddItem = byNat('Odd');
  const redItem = byNat('Red');
  const blackItem = byNat('Black');
  const cardItems = CARDS.map(([, nat]) => byNat(nat));
  const cardsOdds = cardItems[0]?.b || 0;

  const sideBet = (item, buttons) => (
    <div className="lucky7-extra-bets-item-container">
      <div
        className={`lucky7-extra-bets-item${lockTopCls(exposures, item)}${suspCls(item)}`}
        onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
      >
        <div className="casino-odds">{item?.b || 0}</div>
        {' '}
        <div className="casino-buttons">{buttons}</div>
        {' '}
        <CasinoBook value={bookOf(exposures, item)} />
      </div>
      {' '}
      <MinMax item={item} indent={'                    '} />
    </div>
  );

  return (
    <>
      <div className="casino-box low-high-box">
        <div
          className={`low-odds${lockTopCls(exposures, lowItem)}${suspCls(lowItem)}`}
          onClick={isSusp(lowItem) ? undefined : () => handleBet(lowItem.sid, 'back', lowItem.b, lowItem)}
        >
          <div className="casino-odds">{lowItem?.b || 0}</div>
          {' '}
          <div className="text-center casino-buttons"><span>Low Card</span></div>
          {' '}
          <CasinoBook value={bookOf(exposures, lowItem)} />
        </div>
        {' '}
        <div className="text-center lucky7-card">
          <img src={`${CARD_BASE}/6.png`} className="img-fluid" />
        </div>
        {' '}
        <div
          className={`high-odds${lockTopCls(exposures, highItem)}${suspCls(highItem)}`}
          onClick={isSusp(highItem) ? undefined : () => handleBet(highItem.sid, 'back', highItem.b, highItem)}
        >
          <div className="casino-odds">{highItem?.b || 0}</div>
          {' '}
          <div className="text-center casino-buttons"><span>High Card</span></div>
          {' '}
          <CasinoBook value={bookOf(exposures, highItem)} />
        </div>
        {' '}
        <MinMax item={lowItem} indent={'                '} />
      </div>
      {' '}
      <div className="casino-box sidebets-box">
        <div className="lucky7-extra-bets">
          {sideBet(evenItem, 'Even')}
          {' '}
          {sideBet(oddItem, 'Odd')}
          {' '}
          {sideBet(blackItem, (
            <>
              <img src={`${CARD_BASE}/spade.png`} />
              {' '}
              <img src={`${CARD_BASE}/club.png`} />
            </>
          ))}
          {' '}
          {sideBet(redItem, (
            <>
              <img src={`${CARD_BASE}/heart.png`} />
              {' '}
              <img src={`${CARD_BASE}/diamond.png`} />
            </>
          ))}
        </div>
      </div>
      {' '}
      <div className="casino-box cards-box">
        <div className="w-100">
          <div className="casino-odds">{cardsOdds}</div>
          {' '}
          <div className="casino-cards text-center mt-1">
            {CARDS.map(([display], i) => {
              const item = cardItems[i];
              return (
                <div
                  key={display}
                  className="casino-card-item"
                  onClick={isSusp(item) ? undefined : () => handleBet(item.sid, 'back', item.b, item)}
                >
                  <div className={`card-image${suspCls(item)}`}>
                    <img src={`${CARD_BASE}/${display}.png`} />
                  </div>
                  {' '}
                  <CasinoBook value={bookOf(exposures, item)} />
                </div>
              );
            })}
          </div>
        </div>
        {' '}
        <MinMax item={cardItems[0]} indent={'                '} />
      </div>
    </>
  );
});

export default BetTableLucky6;
